'use client';

import * as React from 'react';
import { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  DiagnosisReadDTO,
  DiagnosisConsumptionReadDTO,
  DiagnosisConsumptionDataReadDTO,
  DiagnosisMetersReadDTO,
  DiagnosisRelevantVariablesReadDTO,
  DiagnosisEnergyVectorsReadDTO,
  MetersDataReadDTO,
  VariablesDataReadDTO,
} from '../../../generated/api-client';
import { ValueWithEntityReference } from '../../CrossEntityDateCrudDataGrid';
import {
  ConsumptionCostsChartView,
  computeCorrelation,
  type ChartSeriesConfig,
  type CorrelationResult,
} from './ConsumptionCostsChartView';
import { formatUnitOfMeasurement } from '../../../utils/unitOfMeasurementFormatter';
import { useVectorTypeOptions } from '../../../hooks/useVectorTypeOptions';
import dayjs from 'dayjs';

const MONTH_LABELS = [
  'Gen',
  'Feb',
  'Mar',
  'Apr',
  'Mag',
  'Giu',
  'Lug',
  'Ago',
  'Set',
  'Ott',
  'Nov',
  'Dic',
];
const PERIOD_OPTIONS = [
  { value: 'month', label: 'Per mese' },
  { value: 'barra_anno', label: 'Per anno/mese' },
  { value: 'year', label: 'Per anno' },
  { value: 'reference_period', label: 'Periodo dati generati' },
];

export interface ConsumptionCostsChartsSectionProps {
  diagnosis: DiagnosisReadDTO | null;
  referencePeriodFrom?: string | null;
  referencePeriodTo?: string | null;
  allConsumptions: DiagnosisConsumptionReadDTO[];
  consumptionDataValues: ValueWithEntityReference<DiagnosisConsumptionDataReadDTO>[];
  consumptionTotals: {
    diagnosis_consumption_id?: string;
    year?: number;
    quantity?: number;
    price?: number;
  }[];
  metersFiltered: DiagnosisMetersReadDTO[];
  allMetersData: MetersDataReadDTO[];
  relevantVariables: DiagnosisRelevantVariablesReadDTO[];
  allVariablesData: VariablesDataReadDTO[];
  energyVectors?: DiagnosisEnergyVectorsReadDTO[];
}

function getMeterLabel(m: DiagnosisMetersReadDTO): string {
  const meterCode = m.meter_code || '';
  const customerCode = (m as any).meter_customer_code || '';
  const codeDisplay = customerCode ? `${meterCode}-${customerCode}` : meterCode;
  const udm = m.unit_of_measurement
    ? ` [${formatUnitOfMeasurement(m.unit_of_measurement)}]`
    : ' []';
  return `${codeDisplay || '-'}${udm}`;
}

function getVariableLabel(v: DiagnosisRelevantVariablesReadDTO): string {
  const variable = (v as any).variable || '';
  const tableCode = v.table_code_index || '';
  const udm = v.unit_of_measurement
    ? ` [${formatUnitOfMeasurement(v.unit_of_measurement)}]`
    : ' []';
  const middle = variable && tableCode && tableCode !== variable ? ` - ${tableCode}` : '';
  return `${variable}${middle}${udm}`.trim() || '-';
}

function getConsumptionLabel(c: DiagnosisConsumptionReadDTO): string {
  const code = c.code || c.id || '';
  const udm = c.unit_of_measurement
    ? ` [${formatUnitOfMeasurement(c.unit_of_measurement)}]`
    : ' []';
  return `${code}${udm}`.trim() || '-';
}

export function ConsumptionCostsChartsSection({
  diagnosis,
  referencePeriodFrom,
  referencePeriodTo,
  allConsumptions,
  consumptionDataValues,
  consumptionTotals,
  metersFiltered,
  allMetersData,
  relevantVariables,
  allVariablesData,
  energyVectors = [],
}: ConsumptionCostsChartsSectionProps) {
  const { data: vectorTypeOptions = [] } = useVectorTypeOptions();
  const energyVectorLabelById = useMemo(() => {
    const map = new Map<string, string>();
    energyVectors.forEach(v => {
      if (v.id) {
        const label =
          vectorTypeOptions.find(opt => opt.value === v.vector_type)?.label ??
          v.vector_type ??
          v.code ??
          'Consumi';
        map.set(v.id, label);
      }
    });
    return map;
  }, [energyVectors, vectorTypeOptions]);

  const [groupByX, setGroupByX] = useState<string>('year');
  const [seriesY1, setSeriesY1] = useState<string>('');
  const [seriesY2, setSeriesY2] = useState<string>('');

  useEffect(() => {
    if (seriesY1.endsWith('-price')) setSeriesY1('');
    if (seriesY2.endsWith('-price')) setSeriesY2('');
    if (groupByX.endsWith('-price')) setGroupByX('year');
  }, [seriesY1, seriesY2, groupByX]);

  const getPointsForSeries = useMemo(
    () =>
      function getPoints(key: string): { month: Date; value: number }[] {
        if (!key) return [];
        if (key.startsWith('meter-')) {
          const id = key.replace('meter-', '');
          return allMetersData
            .filter(d => d.meter_id === id && d.month && d.value != null)
            .map(d => ({ month: new Date(d.month), value: Number(d.value) }));
        }
        if (key.startsWith('variable-')) {
          const id = key.replace('variable-', '');
          return allVariablesData
            .filter(d => d.variable_id === id && d.month && d.value != null)
            .map(d => ({ month: new Date(d.month), value: Number(d.value) }));
        }
        if (key.startsWith('consumption-')) {
          const rest = key.replace('consumption-', '');
          const isPrice = rest.endsWith('-price');
          const id = isPrice ? rest.replace('-price', '') : rest;
          if (isPrice) {
            return consumptionTotals
              .filter(t => t.diagnosis_consumption_id === id && t.year != null && t.price != null)
              .map(t => ({ month: new Date(t.year!, 0, 1), value: Number(t.price) }));
          }
          return consumptionDataValues
            .filter(
              v =>
                v.entityId === id &&
                v.value?.month &&
                (v.value as DiagnosisConsumptionDataReadDTO).value != null
            )
            .map(v => ({
              month: new Date((v.value as DiagnosisConsumptionDataReadDTO).month!),
              value: Number((v.value as DiagnosisConsumptionDataReadDTO).value),
            }));
        }
        return [];
      },
    [allMetersData, allVariablesData, consumptionDataValues, consumptionTotals]
  );

  const isPeriodOption = (v: string) => PERIOD_OPTIONS.some(p => p.value === v);
  const isXEntity = groupByX && !isPeriodOption(groupByX);
  const effectiveY1 = seriesY1 || (isXEntity ? groupByX : '');

  const xAxisOptionsGrouped = useMemo(() => {
    const groups: { section: string; options: { value: string; label: string }[] }[] = [
      { section: 'Periodo', options: [...PERIOD_OPTIONS] },
      {
        section: 'Consumi',
        options: allConsumptions
          .filter(c => c.id)
          .map(c => ({ value: `consumption-${c.id}`, label: getConsumptionLabel(c) })),
      },
      {
        section: 'Variabili pertinenti',
        options: relevantVariables
          .filter(v => v.id)
          .map(v => ({ value: `variable-${v.id}`, label: getVariableLabel(v) })),
      },
      {
        section: 'Contatori',
        options: metersFiltered
          .filter(m => m.id)
          .map(m => ({ value: `meter-${m.id}`, label: getMeterLabel(m) })),
      },
    ];
    return groups;
  }, [allConsumptions, relevantVariables, metersFiltered]);

  const seriesOptionsYGrouped = useMemo(() => {
    return [
      {
        section: 'Consumi',
        options: allConsumptions
          .filter(c => c.id)
          .map(c => ({ value: `consumption-${c.id}`, label: getConsumptionLabel(c) })),
      },
      {
        section: 'Variabili pertinenti',
        options: relevantVariables
          .filter(v => v.id)
          .map(v => ({ value: `variable-${v.id}`, label: getVariableLabel(v) })),
      },
      {
        section: 'Contatori',
        options: metersFiltered
          .filter(m => m.id)
          .map(m => ({ value: `meter-${m.id}`, label: getMeterLabel(m) })),
      },
    ];
  }, [allConsumptions, relevantVariables, metersFiltered]);

  const getLabelForValue = (value: string): string => {
    for (const g of xAxisOptionsGrouped) {
      const opt = g.options.find(o => o.value === value);
      if (opt) return opt.label;
    }
    return value;
  };

  const chartData = useMemo(() => {
    const pts1 = getPointsForSeries(effectiveY1);
    const pts2 = getPointsForSeries(seriesY2);
    const ptsX = isXEntity ? getPointsForSeries(groupByX) : null;
    const allMonths = new Map<string, Date>();
    const sourceForMonths = [...(ptsX || []), ...pts1, ...pts2];
    sourceForMonths.forEach(p => {
      const k = dayjs(p.month).format('YYYY-MM');
      if (!allMonths.has(k)) allMonths.set(k, p.month);
    });

    const from = dayjs(referencePeriodFrom);
    const to = dayjs(referencePeriodTo);

    const getValueByMonth = (pts: { month: Date; value: number }[], k: string): number | null => {
      const v = pts.find(p => dayjs(p.month).format('YYYY-MM') === k);
      return v != null ? v.value : null;
    };

    if (isXEntity && ptsX && ptsX.length > 0) {
      const monthsWithX = Array.from(
        new Set(ptsX.map(p => dayjs(p.month).format('YYYY-MM')))
      ).sort();
      const xValues: number[] = [];
      const dataY1: (number | null)[] = [];
      const dataY2: (number | null)[] = [];
      monthsWithX.forEach(k => {
        const xVal = getValueByMonth(ptsX, k);
        if (xVal == null) return;
        xValues.push(xVal);
        dataY1.push(getValueByMonth(pts1, k));
        dataY2.push(getValueByMonth(pts2, k));
      });
      const correlation: CorrelationResult | undefined =
        effectiveY1 && dataY1.some(v => v != null)
          ? computeCorrelation(xValues, dataY1)
          : undefined;
      return {
        kind: 'entity' as const,
        xValues,
        xAxisLabel: getLabelForValue(groupByX),
        dataY1,
        dataY2,
        labelY1: getLabelForValue(effectiveY1),
        labelY2: seriesY2 ? getLabelForValue(seriesY2) : undefined,
        correlation,
      };
    }

    let xKeys: string[];
    if (groupByX === 'reference_period') {
      xKeys = Array.from(allMonths.keys())
        .filter(k => {
          const d = dayjs(k);
          return !d.isBefore(from, 'month') && !d.isAfter(to, 'month');
        })
        .sort();
      if (xKeys.length === 0 && (pts1.length || pts2.length))
        xKeys = Array.from(allMonths.keys()).sort();
    } else if (groupByX === 'year') {
      const years = new Set<string>();
      allMonths.forEach((_, k) => years.add(k.slice(0, 4)));
      xKeys = Array.from(years).sort();
    } else {
      const keysSorted = Array.from(allMonths.keys()).sort();
      if (keysSorted.length === 0) {
        xKeys = [];
      } else {
        const min = dayjs(keysSorted[0] + '-01');
        const max = dayjs(keysSorted[keysSorted.length - 1] + '-01');
        const filled: string[] = [];
        let d = min.startOf('month');
        while (!d.isAfter(max, 'month')) {
          filled.push(d.format('YYYY-MM'));
          d = d.add(1, 'month');
        }
        xKeys = filled;
      }
    }

    const isYearGroup = groupByX === 'year';
    const valueByKey = (pts: { month: Date; value: number }[], k: string): number | null => {
      if (isYearGroup) {
        const y = k.length === 4 ? k : k.slice(0, 4);
        const sum = pts
          .filter(p => dayjs(p.month).format('YYYY') === y)
          .reduce((acc, p) => acc + p.value, 0);
        return pts.some(p => dayjs(p.month).format('YYYY') === y) ? sum : null;
      }
      const v = pts.find(p => dayjs(p.month).format('YYYY-MM') === k);
      return v != null ? v.value : null;
    };

    const labels: string[] = [];
    const dataY1: (number | null)[] = [];
    const dataY2: (number | null)[] = [];
    xKeys.forEach(k => {
      if (isYearGroup) labels.push(k);
      else if (groupByX === 'reference_period') {
        const d = allMonths.get(k) || new Date(k);
        labels.push(dayjs(d).format('MMM YYYY'));
      } else {
        const d = allMonths.get(k) || new Date(k + '-01');
        labels.push(dayjs(d).format('MMM YYYY'));
      }
      dataY1.push(valueByKey(pts1, k));
      dataY2.push(valueByKey(pts2, k));
    });

    const xAxisDates =
      !isYearGroup && groupByX !== 'reference_period'
        ? xKeys.map(k => new Date(k + '-01'))
        : undefined;

    return {
      kind: 'period' as const,
      xKeys: labels,
      xAxisDates,
      dataY1,
      dataY2,
      labelY1: getLabelForValue(effectiveY1),
      labelY2: seriesY2 ? getLabelForValue(seriesY2) : undefined,
      isYearGroup,
    };
  }, [
    groupByX,
    seriesY1,
    seriesY2,
    effectiveY1,
    isXEntity,
    getPointsForSeries,
    referencePeriodFrom,
    referencePeriodTo,
    xAxisOptionsGrouped,
  ]);

  const barChartMonthData = useMemo(() => {
    if (groupByX !== 'barra_anno') return null;
    const barSeriesKey = seriesY1 || seriesY2;
    if (!barSeriesKey) return null;
    const pts = getPointsForSeries(barSeriesKey);
    if (!pts.length) return null;
    const byYearMonth = new Map<string, number>();
    pts.forEach(p => {
      byYearMonth.set(`${dayjs(p.month).year()}-${dayjs(p.month).month() + 1}`, p.value);
    });
    const years = Array.from(new Set(pts.map(p => dayjs(p.month).year()))).sort((a, b) => a - b);
    const seriesByYear = years.map(y => ({
      data: MONTH_LABELS.map((_, i) => byYearMonth.get(`${y}-${i + 1}`) ?? NaN),
      label: String(y),
    }));
    return { seriesByYear };
  }, [groupByX, seriesY1, seriesY2, getPointsForSeries]);

  const hasMonthBarChart =
    groupByX === 'barra_anno' && barChartMonthData && barChartMonthData.seriesByYear.length > 0;

  const seriesConfig: ChartSeriesConfig[] = useMemo(() => {
    const out: ChartSeriesConfig[] = [];
    if (chartData.kind === 'entity') {
      if (chartData.dataY1.some(v => v != null))
        out.push({
          data: chartData.dataY1,
          label: chartData.labelY1,
          color: '#1976d2',
          yAxisId: 'left',
        });
      if (chartData.dataY2.some(v => v != null))
        out.push({
          data: chartData.dataY2,
          label: chartData.labelY2!,
          color: '#d32f2f',
          yAxisId: 'right',
        });
    } else {
      if (chartData.dataY1.some(v => v != null))
        out.push({
          data: chartData.dataY1,
          label: chartData.labelY1,
          color: '#1976d2',
          yAxisId: 'left',
        });
      if (chartData.dataY2?.some(v => v != null))
        out.push({
          data: chartData.dataY2,
          label: chartData.labelY2!,
          color: '#d32f2f',
          yAxisId: 'right',
        });
    }
    return out;
  }, [chartData]);

  const hasChartData =
    (chartData.kind === 'period' && chartData.xKeys.length > 0 && seriesConfig.length > 0) ||
    (chartData.kind === 'entity' && chartData.xValues.length > 0 && seriesConfig.length > 0);

  const variableByName = (name: string): DiagnosisRelevantVariablesReadDTO | undefined => {
    const lower = name.toLowerCase();
    return relevantVariables.find(v => {
      const label = getVariableLabel(v).toLowerCase();
      return label.includes(lower) || (v as any).variable?.toLowerCase().includes(lower);
    });
  };

  const standardChartsPerConsumption = useMemo(() => {
    const prodVar = variableByName('Produzione');
    const tempVar = variableByName('Temperatura');
    return allConsumptions.map(consumption => {
      const consumptionId = consumption.id ? `consumption-${consumption.id}` : null;
      const consumptionLabel = getConsumptionLabel(consumption);
      const vectorId = consumption.diagnosis_energy_vector_id;
      const energyVectorLabel = (vectorId && energyVectorLabelById.get(vectorId)) || 'EE';
      return {
        consumption,
        consumptionLabel,
        consumptionId,
        energyVectorLabel,
        prodId: prodVar?.id ? `variable-${prodVar.id}` : null,
        tempId: tempVar?.id ? `variable-${tempVar.id}` : null,
        prodVar: prodVar ?? null,
        tempVar: tempVar ?? null,
      };
    });
  }, [allConsumptions, relevantVariables, energyVectorLabelById]);

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
        Grafici consumi e costi
      </Typography>

      {/* Accordion: grafici standard per ogni colonna consumi */}
      {standardChartsPerConsumption.map(
        ({
          consumption,
          consumptionLabel,
          consumptionId,
          energyVectorLabel,
          prodId,
          tempId,
          prodVar,
          tempVar,
        }) => (
          <Accordion
            key={consumption.id || consumptionLabel}
            disableGutters
            elevation={0}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">{consumptionLabel}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={3}>
                {/* 1) Consumi mensili: X = Per anno/mese, Y1 = Contatore */}
                {consumptionId && (
                  <Box>
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 1 }}
                    >
                      Consumi mensili {energyVectorLabel}
                    </Typography>
                    <ConsumptionCostsChartView
                      kind="barMonth"
                      seriesByYear={(() => {
                        const pts = getPointsForSeries(consumptionId);
                        if (!pts.length) return [];
                        const byYearMonth = new Map<string, number>();
                        pts.forEach(p => {
                          byYearMonth.set(
                            `${dayjs(p.month).year()}-${dayjs(p.month).month() + 1}`,
                            p.value
                          );
                        });
                        const years = Array.from(new Set(pts.map(p => dayjs(p.month).year()))).sort(
                          (a, b) => a - b
                        );
                        return years.map(y => ({
                          data: MONTH_LABELS.map((_, i) => byYearMonth.get(`${y}-${i + 1}`) ?? NaN),
                          label: String(y),
                        }));
                      })()}
                      height={500}
                    />
                  </Box>
                )}
                {/* 2) Andamento consumi e Produzione: X = Per mese, Y1 = Contatore, Y2 = Produzione */}
                {consumptionId && prodId && (
                  <Box>
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 1 }}
                    >
                      Andamento consumi {energyVectorLabel} e Produzione
                    </Typography>
                    <ConsumptionCostsChartView
                      kind="bar"
                      xAxisData={(() => {
                        const pts1 = getPointsForSeries(consumptionId);
                        const pts2 = getPointsForSeries(prodId);
                        const allMonths = new Set<string>();
                        [...pts1, ...pts2].forEach(p =>
                          allMonths.add(dayjs(p.month).format('YYYY-MM'))
                        );
                        return Array.from(allMonths)
                          .sort()
                          .map(k => {
                            const d = new Date(k + '-01');
                            return d;
                          });
                      })()}
                      xScaleType="point"
                      xAxisMonthStyle
                      series={[
                        {
                          data: (() => {
                            const pts1 = getPointsForSeries(consumptionId);
                            const pts2 = getPointsForSeries(prodId);
                            const allMonths = new Set<string>();
                            [...pts1, ...pts2].forEach(p =>
                              allMonths.add(dayjs(p.month).format('YYYY-MM'))
                            );
                            const keys = Array.from(allMonths).sort();
                            return keys.map(k => {
                              const v = pts1.find(p => dayjs(p.month).format('YYYY-MM') === k);
                              return v != null ? v.value : null;
                            });
                          })(),
                          label: consumptionLabel,
                          color: '#1976d2',
                          yAxisId: 'left',
                        },
                        {
                          data: (() => {
                            const pts1 = getPointsForSeries(consumptionId);
                            const pts2 = getPointsForSeries(prodId);
                            const allMonths = new Set<string>();
                            [...pts1, ...pts2].forEach(p =>
                              allMonths.add(dayjs(p.month).format('YYYY-MM'))
                            );
                            const keys = Array.from(allMonths).sort();
                            return keys.map(k => {
                              const v = pts2.find(p => dayjs(p.month).format('YYYY-MM') === k);
                              return v != null ? v.value : null;
                            });
                          })(),
                          label: prodVar ? getVariableLabel(prodVar) : 'Produzione',
                          color: '#d32f2f',
                          yAxisId: 'right',
                        },
                      ]}
                      y1AsBars
                      height={500}
                    />
                  </Box>
                )}
                {/* 3) Correlazione Consumi e Produzione: X = Contatore, Y1 = Produzione */}
                {consumptionId && prodId && (
                  <Box>
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 1 }}
                    >
                      Correlazione Consumi {energyVectorLabel} e Produzione
                    </Typography>
                    <ConsumptionCostsChartView
                      kind="points"
                      xAxisData={(() => {
                        const ptsX = getPointsForSeries(consumptionId);
                        const ptsY = getPointsForSeries(prodId);
                        const months = new Set<string>();
                        ptsX.forEach(p => months.add(dayjs(p.month).format('YYYY-MM')));
                        const sorted = Array.from(months).sort();
                        return sorted.map(k => {
                          const v = ptsX.find(p => dayjs(p.month).format('YYYY-MM') === k);
                          return v != null ? v.value : NaN;
                        });
                      })()}
                      xScaleType="linear"
                      xAxisLabel={consumptionLabel}
                      series={[
                        {
                          data: (() => {
                            const ptsX = getPointsForSeries(consumptionId);
                            const ptsY = getPointsForSeries(prodId);
                            const months = new Set<string>();
                            ptsX.forEach(p => months.add(dayjs(p.month).format('YYYY-MM')));
                            const sorted = Array.from(months).sort();
                            return sorted.map(k => {
                              const v = ptsY.find(p => dayjs(p.month).format('YYYY-MM') === k);
                              return v != null ? v.value : null;
                            });
                          })(),
                          label: prodVar ? getVariableLabel(prodVar) : 'Produzione',
                          color: '#1976d2',
                        },
                      ]}
                      showOnlyPoints
                      correlation={(() => {
                        const ptsX = getPointsForSeries(consumptionId);
                        const ptsY = getPointsForSeries(prodId);
                        const months = new Set<string>();
                        ptsX.forEach(p => months.add(dayjs(p.month).format('YYYY-MM')));
                        const sorted = Array.from(months).sort();
                        const xV = sorted.map(k => {
                          const v = ptsX.find(p => dayjs(p.month).format('YYYY-MM') === k);
                          return v != null ? v.value : NaN;
                        });
                        const yV = sorted.map(k => {
                          const v = ptsY.find(p => dayjs(p.month).format('YYYY-MM') === k);
                          return v != null ? v.value : null;
                        });
                        return computeCorrelation(xV, yV);
                      })()}
                      height={500}
                    />
                  </Box>
                )}
                {/* 4) Andamento consumi e Temperatura */}
                {consumptionId && tempId && (
                  <Box>
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 1 }}
                    >
                      Andamento consumi {energyVectorLabel} e Temperatura
                    </Typography>
                    <ConsumptionCostsChartView
                      kind="bar"
                      xAxisData={(() => {
                        const pts1 = getPointsForSeries(consumptionId);
                        const pts2 = getPointsForSeries(tempId);
                        const allMonths = new Set<string>();
                        [...pts1, ...pts2].forEach(p =>
                          allMonths.add(dayjs(p.month).format('YYYY-MM'))
                        );
                        return Array.from(allMonths)
                          .sort()
                          .map(k => {
                            const d = new Date(k + '-01');
                            return d;
                          });
                      })()}
                      xScaleType="point"
                      xAxisMonthStyle
                      series={[
                        {
                          data: (() => {
                            const pts1 = getPointsForSeries(consumptionId);
                            const pts2 = getPointsForSeries(tempId);
                            const allMonths = new Set<string>();
                            [...pts1, ...pts2].forEach(p =>
                              allMonths.add(dayjs(p.month).format('YYYY-MM'))
                            );
                            const keys = Array.from(allMonths).sort();
                            return keys.map(k => {
                              const v = pts1.find(p => dayjs(p.month).format('YYYY-MM') === k);
                              return v != null ? v.value : null;
                            });
                          })(),
                          label: consumptionLabel,
                          color: '#1976d2',
                          yAxisId: 'left',
                        },
                        {
                          data: (() => {
                            const pts1 = getPointsForSeries(consumptionId);
                            const pts2 = getPointsForSeries(tempId);
                            const allMonths = new Set<string>();
                            [...pts1, ...pts2].forEach(p =>
                              allMonths.add(dayjs(p.month).format('YYYY-MM'))
                            );
                            const keys = Array.from(allMonths).sort();
                            return keys.map(k => {
                              const v = pts2.find(p => dayjs(p.month).format('YYYY-MM') === k);
                              return v != null ? v.value : null;
                            });
                          })(),
                          label: tempVar ? getVariableLabel(tempVar) : 'Temperatura',
                          color: '#d32f2f',
                          yAxisId: 'right',
                        },
                      ]}
                      y1AsBars
                      height={500}
                    />
                  </Box>
                )}
                {/* 5) Correlazione Consumi e Temperatura */}
                {consumptionId && tempId && (
                  <Box>
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 1 }}
                    >
                      Correlazione Consumi {energyVectorLabel} e Temperatura
                    </Typography>
                    <ConsumptionCostsChartView
                      kind="points"
                      xAxisData={(() => {
                        const ptsX = getPointsForSeries(consumptionId);
                        const months = new Set<string>();
                        ptsX.forEach(p => months.add(dayjs(p.month).format('YYYY-MM')));
                        const sorted = Array.from(months).sort();
                        return sorted.map(k => {
                          const v = ptsX.find(p => dayjs(p.month).format('YYYY-MM') === k);
                          return v != null ? v.value : NaN;
                        });
                      })()}
                      xScaleType="linear"
                      xAxisLabel={consumptionLabel}
                      yAxisLabel={tempVar ? getVariableLabel(tempVar) : 'Temperatura'}
                      series={[
                        {
                          data: (() => {
                            const ptsX = getPointsForSeries(consumptionId);
                            const ptsY = getPointsForSeries(tempId);
                            const months = new Set<string>();
                            ptsX.forEach(p => months.add(dayjs(p.month).format('YYYY-MM')));
                            const sorted = Array.from(months).sort();
                            return sorted.map(k => {
                              const v = ptsY.find(p => dayjs(p.month).format('YYYY-MM') === k);
                              return v != null ? v.value : null;
                            });
                          })(),
                          label: tempVar ? getVariableLabel(tempVar) : 'Temperatura',
                          color: '#1976d2',
                        },
                      ]}
                      showOnlyPoints
                      correlation={(() => {
                        const ptsX = getPointsForSeries(consumptionId);
                        const ptsY = getPointsForSeries(tempId);
                        const months = new Set<string>();
                        ptsX.forEach(p => months.add(dayjs(p.month).format('YYYY-MM')));
                        const sorted = Array.from(months).sort();
                        const xV = sorted.map(k => {
                          const v = ptsX.find(p => dayjs(p.month).format('YYYY-MM') === k);
                          return v != null ? v.value : NaN;
                        });
                        const yV = sorted.map(k => {
                          const v = ptsY.find(p => dayjs(p.month).format('YYYY-MM') === k);
                          return v != null ? v.value : null;
                        });
                        return computeCorrelation(xV, yV);
                      })()}
                      height={500}
                    />
                  </Box>
                )}
                {!consumptionId && (
                  <Typography variant="body2" color="text.secondary">
                    Nessun dato per questo consumo.
                  </Typography>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>
        )
      )}

      {/* Accordion: Grafico personalizzabile */}
      <Accordion
        defaultExpanded
        disableGutters
        elevation={0}
        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mt: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">Grafico personalizzabile</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 260 }}>
              <InputLabel>Asse X</InputLabel>
              <Select value={groupByX} label="Asse X" onChange={e => setGroupByX(e.target.value)}>
                {xAxisOptionsGrouped.flatMap(g => [
                  <ListSubheader key={g.section} sx={{ fontWeight: 600 }}>
                    {g.section}
                  </ListSubheader>,
                  ...g.options.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  )),
                ])}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 260 }}>
              <InputLabel>Asse Y1</InputLabel>
              <Select value={seriesY1} label="Asse Y1" onChange={e => setSeriesY1(e.target.value)}>
                <MenuItem value="">— Nessuna —</MenuItem>
                {seriesOptionsYGrouped.flatMap(g => [
                  <ListSubheader key={g.section} sx={{ fontWeight: 600 }}>
                    {g.section}
                  </ListSubheader>,
                  ...g.options.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  )),
                ])}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 260 }}>
              <InputLabel>Asse Y2</InputLabel>
              <Select value={seriesY2} label="Asse Y2" onChange={e => setSeriesY2(e.target.value)}>
                <MenuItem value="">— Nessuna —</MenuItem>
                {seriesOptionsYGrouped.flatMap(g => [
                  <ListSubheader key={g.section} sx={{ fontWeight: 600 }}>
                    {g.section}
                  </ListSubheader>,
                  ...g.options.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  )),
                ])}
              </Select>
            </FormControl>
          </Stack>
          {!hasChartData && !hasMonthBarChart && (
            <Typography color="text.secondary">
              Seleziona almeno una serie (Asse Y1 o Y2) per visualizzare il grafico.
            </Typography>
          )}
          {hasMonthBarChart && barChartMonthData && (
            <ConsumptionCostsChartView
              kind="barMonth"
              seriesByYear={barChartMonthData.seriesByYear}
              height={500}
            />
          )}
          {hasChartData && !hasMonthBarChart && chartData.kind === 'entity' && (
            <ConsumptionCostsChartView
              kind="points"
              xAxisData={chartData.xValues}
              xScaleType="linear"
              xAxisLabel={chartData.xAxisLabel}
              yAxisLabel={chartData.labelY1}
              series={seriesConfig}
              correlation={chartData.correlation}
              showOnlyPoints
              height={500}
            />
          )}
          {hasChartData && !hasMonthBarChart && chartData.kind === 'period' && (
            <ConsumptionCostsChartView
              kind="bar"
              xAxisData={chartData.xAxisDates ?? chartData.xKeys}
              xScaleType="point"
              xAxisMonthStyle={!!chartData.xAxisDates}
              series={seriesConfig}
              y1AsBars
              height={500}
            />
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
